import React, { useRef, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import useDontMountAtFirst from '../../../../core/hooks/useDontMountAtFirst';
import { PayloadViewerDialog } from '../../../../modal/components/payload-viewer-dialog/PayloadViewerDialog';
import { HdCoordinatedScroll } from '../../../../a11y/scroll/components/coordinated-scroll/HdCoordinatedScroll';
import ScrollToTopButton from '../../../../a11y/scroll/components/scroll-to-top-button/ScrollToTopButton';
import HdModal from '../../../../modal/components/modal/HdModal';
import QueryTableCell from '../query-table-cell/QueryTableCell';
import { getFormattedOutput } from '../../utils/utils';

export interface QueryOutputTableProps {
  output: any;
  useVirtualScrolling?: boolean;
  canShowJSONDialog?: boolean;
  renderScrollTopBtn?: boolean;
}

const PAGE_SIZE = 20;

export default function QueryOutputTable({
  output,
  useVirtualScrolling = false,
  canShowJSONDialog = true,
  renderScrollTopBtn= true
}: QueryOutputTableProps) {
  const [ coordinatedScroll, setCoordinatedScroll ] = useState(null);
  const [ jsonDialogContent, setJSONDialogContent ] = useState(null);
  const editorRef = useRef(null);

  const _getFormattedOutput = () => {
    return {
      ...output,
      rows: getFormattedOutput(output.rows, output.headers)
    };
  };

  const getInitialRenderedRows = () => {
    if (useVirtualScrolling) {
      return formattedOutput.rows.slice(0, getNextPageSize(0));
    }

    return formattedOutput.rows;
  };

  const getNextPageSize = (startIndex) => {
    const remainingRowsCount = output.rows.length - startIndex;

    return Math.min(PAGE_SIZE, remainingRowsCount);
  };

  const [ formattedOutput, setFormattedOutput ] = useState(_getFormattedOutput());
  const [ renderedRows, setRenderedRows ] = useState(getInitialRenderedRows());

  useDontMountAtFirst(() => {
    setFormattedOutput(_getFormattedOutput());
  }, [output]);

  useDontMountAtFirst(() => {
    setRenderedRows(getInitialRenderedRows());
  }, [formattedOutput, useVirtualScrolling]);

  const [sentryRef] = useInfiniteScroll({
    loading: false,
    hasNextPage: renderedRows.length < output.rows.length,
    onLoadMore: () => {
      setRenderedRows((rows) => [
        ...rows,
        ...output.rows.slice(
          renderedRows.length,
          renderedRows.length + getNextPageSize(renderedRows.length)
        )
      ]);
    },
    disabled: !useVirtualScrolling,
    rootMargin: '0px 0px 100px 0px'
  });

  const openJSONDialog = (cell, cellIndex) => {
    setJSONDialogContent({ cell, title: output.headers[cellIndex].name });
  };

  const closeJSONDialog = () => {
    setJSONDialogContent(null);
  };

  return (
    <HdCoordinatedScroll elRef={coordinatedScroll}>
      <div
        className='h-100 overflow-auto rr-block'
        id='table_scrollable_container'
        ref={el => {
          setCoordinatedScroll(el);
        }}
      >
        <table className='data-table'>
          <thead>
            <tr>
              {formattedOutput.headers.map(({ name }, index) => (
                <th key={index}>
                  <QueryTableCell value={name} />
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {!formattedOutput.rows.length && (
              <tr>
                <td colSpan={formattedOutput.headers.length}>No Rows Found</td>
              </tr>
            )}

            {renderedRows.map((rowData, rowIndex) => (
              <tr key={rowIndex}>
                {rowData.map((cell, cellIndex) => (
                  <td key={cellIndex}>
                    <QueryTableCell
                      value={cell}
                      canShowJSONDialog={canShowJSONDialog}
                      openJSONDialog={() => openJSONDialog(cell, cellIndex)}
                    />
                  </td>
                ))}
              </tr>
            ))}

            <tr ref={sentryRef} />
          </tbody>
        </table>

        {renderScrollTopBtn && <ScrollToTopButton scrollViewportId='table_scrollable_container' />}
      </div>

      <HdModal
        open={!!jsonDialogContent}
        onClose={closeJSONDialog}
        placement='top'
        styling={{ width: '760px' }}
        onTransitionEnd={() => { editorRef.current.refresh(); }}
      >
        <PayloadViewerDialog
          title={jsonDialogContent?.title}
          onNegativeClick={closeJSONDialog}
          getPayloadPromise={() => Promise.resolve({ payload: jsonDialogContent.cell.json || JSON.stringify({}) })}
          editorRef={editorRef}
        />
      </HdModal>
    </HdCoordinatedScroll>
  );
}
